/* eslint-disable @typescript-eslint/no-explicit-any */

/* eslint-disable @typescript-eslint/no-unused-vars */
//constants
import { PlusOutlined } from '@ant-design/icons'
import { Modal, Progress, Tooltip, Upload, UploadProps, message } from 'antd'
import type { RcFile, UploadFile } from 'antd/lib/upload'
import axios, { AxiosRequestHeaders } from 'axios'
import jwt_decode from "jwt-decode";

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { v4 as uuidv4 } from 'uuid';
import { DndProvider, useDrag, useDrop } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
interface Response<T> {
  data: T
}

const type = 'DragableUploadList';

export enum AllowedFormats {
  'JPEG' = 'image/jpeg',
  'JPG' = 'image/jpg',
  'PNG' = 'image/png',
  'WEBP' = 'image/webp',
  'SVG' = 'image/svg+xml',
  'GIF' = 'image/gif',
  'MP4' = 'video/mp4',
  'MP3_AUDIO' = 'audio/mpeg',
  'MP3_VIDEO' = 'video/mpeg',
}
interface Props {
  preSignUrl: string
  min: number
  max: number
  value?: string[]
  onChange?: (value: any) => void
  cdnUrl: string
  fileList?: any
  preserveFilename?: boolean
  preserveRawFilename?: boolean
  allowedFileExtension?: string[]
  considerOnlyExtension?: boolean
  headers?: any
  maxSizeInKiloBytes?: number
  templeCode?: string
  disabled?: boolean
  multiple?: boolean
  isM3U8?: boolean
}
const getBase64 = (file: RcFile): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result as string)
    reader.onerror = error => reject(error)
  })
interface DragableUploadListItemProps {
  originNode: React.ReactElement<any, string | React.JSXElementConstructor<any>>;
  file: UploadFile;
  fileList: UploadFile[];
  moveRow: (dragIndex: any, hoverIndex: any) => void;
}
interface DragItem {
  index: number;
  type: string;
}

const DragableUploadListItem = ({
  originNode,
  moveRow,
  file,
  fileList,
}: DragableUploadListItemProps) => {
  const ref = useRef<HTMLDivElement>(null);
  const index = fileList.indexOf(file);
  const [{ isOver, dropClassName }, drop] = useDrop({
    accept: type,
    collect: monitor => {
      const dragItem = monitor.getItem() as DragItem | undefined;
      if (dragItem?.index === index) {
        return {};
      }
      return {
        isOver: monitor.isOver(),
        dropClassName: dragItem && dragItem.index < index ? ' drop-over-downward' : ' drop-over-upward',
      };
    },
    drop: (item: DragItem) => {
      moveRow(item.index, index);
    },
  });
  const [, drag] = useDrag({
    type,
    item: { index, type },
    collect: monitor => ({
      isDragging: monitor.isDragging(),
    }),
  });
  drop(drag(ref));
  const errorNode = <Tooltip title="Upload Error">{originNode.props.children}</Tooltip>;
  return (
    <div
      ref={ref}
      className={`ant-upload-draggable-list-item ${isOver ? dropClassName : ''}`}
      style={{ cursor: 'move', height: '100%' }}
    >
      {file.status === 'error' ? errorNode : originNode}
    </div>
  );
};

const S3Upload: React.FC<Props> = ({
  preSignUrl,
  cdnUrl,
  onChange,
  max = 1,
  fileList,
  preserveFilename,
  allowedFileExtension,
  preserveRawFilename,
  considerOnlyExtension = false,
  headers,
  maxSizeInKiloBytes = 200000,
  templeCode = null,
  disabled = false,
  multiple = false,
  isM3U8 = false
}) => {
  const [uploadedFileList, setUploadedFile] = useState<any[]>([])
  const [previewVisible, setPreviewVisible] = useState(false)
  const [uploadStarted, setUploadStarted] = useState(false)
  const [previewImage, setPreviewImage] = useState('')
  const [previewTitle, setPreviewTitle] = useState('')
  const [progress, setProgress] = useState<number>(0)
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: number }>({})
  const [uploading, setUploading] = useState(false)

  useEffect(() => {
    if (fileList && !uploadStarted) {
      // Convert fileList to an array if it is string
      const fileArray = Array.isArray(fileList) ? fileList : [fileList];

      const form =
        fileArray?.map((item: string) => {
          const lastIndex = item?.lastIndexOf('/');
          const fileName = item?.slice(lastIndex + 1);
          const isFullUrl = item?.startsWith('http://') || item?.startsWith('https://');
          return {
            uid: uuidv4(),
            status: 'done',
            name: considerOnlyExtension ? item : fileName,
            url: isFullUrl || item?.includes('/content/temple/') || considerOnlyExtension
              ? item
              : `${cdnUrl}/${fileName}`,
            key: fileName,
          };
        }) || [];

      setUploadedFile(form);
      onChange?.(fileArray);
    }
    if (!fileList) {
      onChange?.([])
    }
  }, [fileList])

  const handleUpload = async (file: RcFile) => {
    let fileName = '';
    if (!file?.name) {
      return false;
    }

    if (preserveFilename) {
      const split = file?.name.split('.')
      const splicedStr = split
        .slice(0, split.length - 1)
        .join('.')
        .replace(/ /g, '_')
      fileName = `${splicedStr}_${Date.now()
        .toString()
        .slice(-4)
        }.${file?.name?.split('.')?.pop()}`
      if (allowedFileExtension?.length) {
        if (!allowedFileExtension?.includes(file?.type)) {
          message.warn("Please upload proper file format.")
          return false;
        }
      }
    } else if (preserveRawFilename) {
      fileName = file?.name
      if (fileName?.split('').filter((item) => item.includes(".")).length > 1) {
        message.warn("File name should contain only 1 dot at last. Please alter and upload again")
        return false;
      }
      if (allowedFileExtension?.length) {
        if (!allowedFileExtension?.includes(file?.type)) {
          message.warn("Please upload proper file format.")
          return false;
        }
      }
    } else {
      const fileExtension = file?.name?.split('.')?.pop();
      const baseName = isM3U8 ? `${+new Date()}_processed` : `${+new Date()}`;
      fileName = `${baseName}.${fileExtension}`;
    }

    let actionUrl = preSignUrl || "";
    actionUrl = actionUrl + (considerOnlyExtension ? file?.name?.split('.')?.pop() : fileName)

    if (templeCode) {
      actionUrl = `${actionUrl}&temple_code=${templeCode}&asset_type=avatar`
    }

    try {
      const token = JSON.parse(localStorage.getItem('userinfo') || '{}')?.access_token
      const jwtRes: any = token ? jwt_decode(token) : {}
      const _headers: AxiosRequestHeaders = {
        'x-afb-app-ver': '1.0.0',
        'x-afb-device-id': window.navigator.userAgent,
        'x-afb-platform': 'web',
        'Authorization': 'Bearer ' + token
      }
      if (jwtRes.sub) {
        _headers['X-AFB-R-UID'] = jwtRes.sub;
      }

      const presignedUrl = await axios.get<Response<any>>(
        actionUrl, { headers: { ...headers, ..._headers } }
      )

      const uploadId = uuidv4();

      // Update progress for specific file and calculate overall progress
      const updateProgress = (percent: number) => {
        setUploadingFiles(prev => {
          const newState = { ...prev, [uploadId]: percent };
          const filesInProgress = Object.values(newState).filter(v => v > 0);
          const overallProgress = filesInProgress.length > 0
            ? Math.floor(filesInProgress.reduce((sum, val) => sum + val, 0) / filesInProgress.length)
            : 0;

          setProgress(overallProgress);
          return newState;
        });
      };

      const res = await axios.put(presignedUrl?.data?.data?.url, file, {
        headers: { 'Content-Type': file?.type },
        onUploadProgress: (event: any) => {
          const percent = Math.floor((event.loaded / event.total) * 100)
          updateProgress(percent);
          if (percent === 100) {
            setTimeout(() => updateProgress(0), 1000)
          }
        },
      })

      const newFileInfo = {
        uid: `${uuidv4()}_${fileName}`,
        name: considerOnlyExtension ? presignedUrl?.data?.data?.attachmentId : fileName,
        status: 'done',
        ...(presignedUrl?.data?.data?.path?.startsWith('/content/temple/') ? {
          url: considerOnlyExtension ? cdnUrl + new URL(presignedUrl?.data?.data?.url).pathname : `${cdnUrl}${presignedUrl?.data?.data?.path}`,
        } : {
          url: considerOnlyExtension ? cdnUrl + new URL(presignedUrl?.data?.data?.url).pathname : `${cdnUrl}/${fileName}`,
        }),
        type: file?.type,
      };

      setUploadedFile(prevList => {
        const newList = [...prevList, newFileInfo];
        onChange?.(newList.map(item => item.url));
        return newList;
      });

      setUploadStarted(true);
      return true;
    } catch (error) {
      const errorFileInfo = {
        uid: `${uuidv4()}_${fileName}`,
        name: fileName,
        status: 'error',
        response: 'upload error',
        url: `${cdnUrl}/${fileName}`,
        type: file?.type,
      };

      setUploadedFile(prevList => {
        const newList = [...prevList, errorFileInfo];
        return newList;
      });

      message.error(`${file.name} file upload failed.`);
      return false;
    }
  };

  const uploadProps: UploadProps = {
    accept: allowedFileExtension?.join(','),
    multiple: multiple,
    fileList: uploadedFileList,
    listType: 'picture-card',
    beforeUpload: (file: RcFile) => {
      if (allowedFileExtension?.length) {
        if (!allowedFileExtension?.includes(file?.type)) {
          message.warn(`Please upload proper file formats like: ${allowedFileExtension.join(",")}`)
          return false
        } else if ((file?.size / 1024) > maxSizeInKiloBytes) {
          message.warn(`Please upload files less than: ${maxSizeInKiloBytes} Kilo Bytes`)
          return false
        }
      }

      // If multiple is true and we're selecting files that would exceed max
      if (multiple && uploadedFileList.length + 1 > max) {
        message.warn(`You can only upload a maximum of ${max} files.`);
        return false;
      }

      return true;
    },
    async customRequest({ file, onError, onSuccess }: any) {
      const isRcFile = file instanceof File || file instanceof Blob;
      const rcFile = isRcFile ? file : file.originFileObj;

      if (multiple) {
        setUploading(true);
      }

      try {
        const result = await handleUpload(rcFile);
        if (result) {
          onSuccess(file);
        } else {
          onError('Upload failed');
        }
      } catch (err) {
        onError(err);
      } finally {
        if (multiple) {
          const activeUploads = Object.values(uploadingFiles).filter(val => val > 0 && val < 100).length;
          if (activeUploads === 0) {
            setUploading(false);
            setTimeout(() => {
              setProgress(0);
              setUploadingFiles({});
            }, 1500);
          }
        }
      }
    },
    onRemove: (file: any) => {
      const filteredFileList = uploadedFileList?.filter(
        (item: any) => item.uid !== file.uid,
      )
      setUploadedFile([...filteredFileList])
      const uploadedList = filteredFileList?.map((item: any) => item.url)
      onChange?.(uploadedList)
    },
    onPreview: async (file: any) => {
      if (file.url) {
        window.open(file.url, '_blank');
        return;
      }

      // Fall back to preview modal for files without URLs
      if (!file.url && !file.preview) {
        file.preview = await getBase64(file.originFileObj as RcFile);
      }

      setPreviewImage(file.url || (file.preview as string));
      setPreviewVisible(true);
      setPreviewTitle(
        file.name || file.url!.substring(file.url!.lastIndexOf('/') + 1),
      );
    },
  };

  const moveRow = useCallback(
    (dragIndex: number, hoverIndex: number) => {
      const newFileList = [...uploadedFileList];
      const dragItem = newFileList[dragIndex];
      newFileList.splice(dragIndex, 1);
      newFileList.splice(hoverIndex, 0, dragItem);

      setUploadedFile(newFileList);
      onChange?.(newFileList.map((item: any) => item.url));
      setUploadStarted(true);
    },
    [uploadedFileList, onChange],
  );

  const handleCancel = () => setPreviewVisible(false);

  const renderProgressBar = () => {
    return progress > 0 ? <Progress percent={progress} /> : null;
  };

  return (
    <DndProvider backend={HTML5Backend}>
      <Upload
        disabled={disabled || uploading}
        {...uploadProps}
        itemRender={(originNode, file, currFileList) => (
          <DragableUploadListItem
            originNode={originNode}
            file={file}
            fileList={currFileList}
            moveRow={moveRow}
          />
        )}
      >
        {uploadedFileList?.length < max ? (
          <div>
            <PlusOutlined />
            <div style={{ marginTop: 8 }}>Upload</div>
          </div>
        ) : (
          ''
        )}
      </Upload>
      {renderProgressBar()}
      <Modal
        visible={previewVisible}
        title={previewTitle}
        footer={null}
        onCancel={handleCancel}
      >
        <img alt='image preview' style={{ width: '100%' }} src={previewImage} />
      </Modal>
    </DndProvider>
  )
}

export default S3Upload